import React from 'react';
import { Chip, ChipTab } from '../../lib/types';
import { formatDate, getAdopterDisplayName } from '../../lib/utils';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import styles from './Chips.module.scss';

interface ChipRowProps {
  chip: Chip;
  tab: ChipTab;
  onRegister: (chipId: string) => void;
  onDetail: (chip: Chip) => void;
  onTransfer: (chip: Chip) => void;
  onRecall: (chip: Chip) => void;
}

function StockActions({
  chip,
  onDetail,
  onRegister,
  onTransfer,
}: {
  chip: Chip;
  onDetail: (chip: Chip) => void;
  onRegister: (chipId: string) => void;
  onTransfer: (chip: Chip) => void;
}) {
  if (chip.petName) {
    return (
      <div className={styles.rowActions}>
        <Button variant="ghost" size="sm" onClick={() => onDetail(chip)}>
          View
        </Button>
        <Button variant="green" size="sm" onClick={() => onTransfer(chip)}>
          Add Owner
        </Button>
      </div>
    );
  }
  return (
    <Button variant="ghost" size="sm" onClick={() => onRegister(chip.id)}>
      Register
    </Button>
  );
}

function RegisteredActions({
  chip,
  onDetail,
  onRecall,
}: {
  chip: Chip;
  onDetail: (chip: Chip) => void;
  onRecall: (chip: Chip) => void;
}) {
  return (
    <div className={styles.rowActions}>
      <Button variant="ghost" size="sm" onClick={() => onDetail(chip)}>
        View
      </Button>
      {chip.adopterFirstName && (
        <Button variant="danger" size="sm" onClick={() => onRecall(chip)}>
          Clear
        </Button>
      )}
    </div>
  );
}

export default function ChipRow({ chip, tab, onRegister, onDetail, onTransfer, onRecall }: ChipRowProps) {
  // Search results: combined row with per-chip status and actions
  if (tab === 'all') {
    const isStock = chip.status === 'stock';
    return (
      <tr>
        <td>
          <div className={styles.chipIdCell}>{chip.fullChipId || chip.id}</div>
          <div className={styles.chipIdShort}>{chip.id}</div>
        </td>
        <td>{chip.petName || '\u2014'}</td>
        <td>{chip.breed || '\u2014'}</td>
        <td>{isStock ? '\u2014' : getAdopterDisplayName(chip)}</td>
        <td>{formatDate(isStock ? chip.batchDate : chip.registeredDate)}</td>
        <td>
          {isStock ? (
            '\u2014'
          ) : (
            <StatusBadge variant={chip.registrationSource === 'ShelterLuv' ? 'success' : 'info'}>
              {chip.registrationSource || 'Fi Nano'}
            </StatusBadge>
          )}
        </td>
        <td>
          <StatusBadge variant={isStock ? 'neutral' : 'success'}>{isStock ? 'In Stock' : 'Registered'}</StatusBadge>
        </td>
        <td>
          {isStock ? (
            <StockActions chip={chip} onDetail={onDetail} onRegister={onRegister} onTransfer={onTransfer} />
          ) : (
            <RegisteredActions chip={chip} onDetail={onDetail} onRecall={onRecall} />
          )}
        </td>
      </tr>
    );
  }

  if (tab === 'stock') {
    return (
      <tr>
        <td>
          <div className={styles.chipIdCell}>{chip.fullChipId || chip.id}</div>
          <div className={styles.chipIdShort}>{chip.id}</div>
        </td>
        <td>{chip.petName || '\u2014'}</td>
        <td>{chip.breed || '\u2014'}</td>
        <td>{formatDate(chip.batchDate)}</td>
        <td>
          <StatusBadge variant="neutral">In Stock</StatusBadge>
        </td>
        <td>
          <StockActions chip={chip} onDetail={onDetail} onRegister={onRegister} onTransfer={onTransfer} />
        </td>
      </tr>
    );
  }

  // Registered tab
  return (
    <tr>
      <td className={styles.chipIdCell}>{chip.fullChipId || chip.id}</td>
      <td>{chip.petName || '\u2014'}</td>
      <td>{chip.breed || '\u2014'}</td>
      <td>{getAdopterDisplayName(chip)}</td>
      <td>{formatDate(chip.registeredDate)}</td>
      <td>
        <StatusBadge variant={chip.registrationSource === 'ShelterLuv' ? 'success' : 'info'}>
          {chip.registrationSource || 'Fi Nano'}
        </StatusBadge>
      </td>
      <td>
        <RegisteredActions chip={chip} onDetail={onDetail} onRecall={onRecall} />
      </td>
    </tr>
  );
}
